import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { fullStates } from 'config/states.constants'
import { useEffect, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Section } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const CONFIG_KEY = 'hmdaFilingInformation'

interface IHmdaFilingInformationResponse {
  config: Record<string, string>
  federalAgencyOptions: Record<string, string>
}

const sections: { title: string; fields: string[] }[] = [
  {
    title: 'Filing Institution',
    fields: ['recordIdentifier', 'financialInstitutionName', 'federalAgency', 'taxId', 'lei'],
  },
  {
    title: 'Reporting Period',
    fields: ['calendarYear', 'calendarQuarter', 'totalEntries'],
  },
  {
    title: 'Contact Information',
    fields: [
      'contactName',
      'contactPhone',
      'contactEmail',
      'contactStreetAddress',
      'contactCity',
      'contactState',
      'contactZip',
    ],
  },
]

const buildInputs = (federalAgencyOptions: Record<string, string>): Record<string, InputType> => ({
  recordIdentifier: {
    inputType: 'text',
    title: 'Record Identifier',
    readOnly: true,
    tooltip: 'Always 1 for the Transmittal Sheet.',
  },
  financialInstitutionName: { inputType: 'text', title: 'Financial Institution Name', span: 2 },
  federalAgency: {
    inputType: 'select',
    title: 'Federal Agency',
    options: federalAgencyOptions,
    hasDefaultOption: true,
    span: 2,
    tooltip: 'Exported as the agency code shown before each name.',
  },
  taxId: { inputType: 'text', type: 'entityTaxID', title: 'Federal Taxpayer Identification Number' },
  lei: {
    inputType: 'text',
    title: 'Legal Entity Identifier (LEI)',
    readOnly: true,
    span: 2,
    tooltip: 'Maintained in Configuration > Global Settings > Setup.',
  },
  calendarYear: { inputType: 'text', type: 'number', title: 'Calendar Year' },
  calendarQuarter: {
    inputType: 'text',
    title: 'Calendar Quarter',
    readOnly: true,
    tooltip: 'Always 4 for the annual filing.',
  },
  totalEntries: { inputType: 'text', type: 'number', title: 'Total Number of Entries Contained in Submission' },
  contactName: { inputType: 'text', title: "Contact Person's Name" },
  contactPhone: { inputType: 'text', type: 'phone', title: "Contact Person's Telephone Number" },
  contactEmail: { inputType: 'text', type: 'email', title: "Contact Person's E-mail Address", span: 2 },
  contactStreetAddress: { inputType: 'text', title: "Contact Person's Office Street Address", span: 2 },
  contactCity: { inputType: 'text', title: "Contact Person's Office City" },
  contactState: {
    inputType: 'select',
    title: "Contact Person's Office State",
    options: fullStates,
    hasDefaultOption: true,
  },
  contactZip: { inputType: 'text', title: "Contact Person's Office ZIP Code" },
})

export const HmdaFilingInformationConfiguration = () => {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [isChanged, setIsChanged] = useState<boolean | undefined>(undefined)

  const applyResponse = (res: IHmdaFilingInformationResponse) => {
    const next = buildInputs(res?.federalAgencyOptions || {})
    Object.keys(next).forEach((key) => {
      next[key].value = res?.config?.[key] ?? ''
      next[key].error = ''
    })
    setInputs(next)
  }

  useEffect(() => {
    setLoading(true)
    getAdminConfig(CONFIG_KEY)
      .then(applyResponse)
      .finally(() => setLoading(false))
  }, [])

  const onChange = (key: string, value: any) => {
    setInputs((prev) => {
      const input = { ...prev[key] }
      input.value = InputConvert(input, value)
      input.error = input.value ? InputValidate(input) : ''
      return { ...prev, [key]: input }
    })
    setIsChanged(true)
  }

  const onSubmit = async () => {
    const invalid = Object.values(inputs).find((input) => input.error)
    if (invalid) {
      toast(`${invalid.title}: ${invalid.error}`, { type: 'error' })
      return
    }

    const payload: Record<string, string> = {}
    Object.keys(inputs).forEach((key) => {
      if (!inputs[key].readOnly) payload[key] = inputs[key].value ?? ''
    })

    setLoading(true)
    try {
      applyResponse(await setAdminConfig(CONFIG_KEY, payload))
      toast('Saved successfully', { type: 'success' })
      setIsChanged(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={loading} />
      <Prompt when={!!isChanged} message="Are you sure you want to leave without saving your changes?" />

      <p className="text-sm text-gray-500 mb-4">
        Filing institution, reporting period and contact details for the HMDA Transmittal Sheet.
      </p>

      {sections.map(({ title, fields }) => (
        <div key={title} className="mb-6">
          <Section title={title} />
          <div className="grid gap-2 gap-y-4 md:grid-cols-4 sm:grid-cols-2 grid-cols-1">
            {fields.map((key) => {
              const input = inputs[key]
              if (!input) return null
              return (
                <div className={`col-span-${input.span || 1}`} key={key}>
                  <RenderInput Key={key} input={{ ...input, history: false }} onChange={onChange} />
                </div>
              )
            })}
          </div>
        </div>
      ))}

      <SaveChanges show={!!isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
